<template>
  <div class="cash-sources-stat">
    <div class="stat-menu-abc">
      <v-container fluid>
        <stat-top-filter
          :filial-default="[globalFilialId]"
          last-month
          multiple-filial-select
          @applyFilter="applyFilter"
        >
          <template #afterApply="{ filter }">
            <v-btn
              style="border-radius: 10px"
              height="48px"
              depressed
              color="#D3D5D9"
              :loading="reportDownloading"
              @click="downloadReport(filter)"
            >
              Выгрузить в Excel
            </v-btn>
          </template>
        </stat-top-filter>

        <loader v-if="loading" />
        <template v-else>
          <div class="stat-menu-abc-table">
            <v-data-table
              v-if="statisticData.length"
              fixed-header
              :headers="headers"
              :page="page"
              :items="statisticData"
              :items-per-page="itemsPerPage"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :footer-props="footerProps"
              :server-items-length="itemsLength"
              class="statistic-datatable headers-nowrap orange-header"
              @update:options="loadItems"
            >
              <template #item="{ item }">
                <tr>
                  <td>
                    <div class="user-column">
                      <avatar :user="item.user" class="mr-3" />
                      <div>{{ item.user.name }}</div>
                    </div>
                  </td>
                  <td class="text-center">
                    {{ item.workScheduleCount }}
                  </td>
                  <td class="text-center">
                    {{ formatWorkingTime(item.workingTime) }}
                  </td>
                  <td class="text-center">
                    {{ formatCount(item.acceptedOrdersCount) }}
                  </td>
                  <td class="text-center">
                    {{ item.upsellingRevenue | amountWithCurrency0 }}
                  </td>
                  <td class="text-center">
                    {{ item.upsellingRevenuePercentage }}%
                  </td>
                  <td class="text-center">
                    {{ formatAvgConfirmTime(item.avgConfirmTime) }}
                  </td>
                </tr>
              </template>
            </v-data-table>
            <div v-else class="no-data-label">
              Нет записей за выбранный период
            </div>
          </div>
        </template>

        <v-row>
          <v-col md="4" cols="12">
            <chart-container
              style="height: 100%"
              :loading="loading"
              :no-data="isEmptyProportion(proportionByRevenue)"
            >
              <template #chart>
                <proportion-chart
                  title="Сумма заказов (ТОП 3)"
                  :items="proportionByRevenue"
                  :colors="revenueProportionChartColors"
                  legend-container-id="proportion-by-revenue"
                />
              </template>
            </chart-container>
          </v-col>
          <v-col md="4" cols="12">
            <chart-container
              style="height: 100%"
              :loading="loading"
              :no-data="isEmptyProportion(proportionByWorkingTime)"
            >
              <template #chart>
                <proportion-chart
                  title="Часы (ТОП 3)"
                  :items="proportionByWorkingTime"
                  :colors="workingTimeProportionChartColors"
                  legend-container-id="proportion-by-working-time"
                />
              </template>
            </chart-container>
          </v-col>
          <v-col md="4" cols="12">
            <chart-container
              style="height: 100%"
              :loading="loading"
              :no-data="isEmptyProportion(proportionByUpsellingRevenue)"
            >
              <template #chart>
                <proportion-chart
                  title="Допродажи % (ТОП 3)"
                  :items="proportionByUpsellingRevenue"
                  :colors="upsellingRevenueProportionChartColors"
                  legend-container-id="proportion-by-upselling-revenue"
                />
              </template>
            </chart-container>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
  import Avatar from '@/components/administrator/work-shift/staff/Avatar'
  import StatTopFilter from '@/components/stat/StatTopFilter.vue'
  import Loader from '@/components/loader/Loader'
  import { mapGetters } from 'vuex'
  import SnackbarMessage from '@/mixins/snackbar-message'
  import permissions from '@/mixins/permissions'
  import employeesApi from '@/api/statistics/call-center/employees'
  import employeesWorkScheduleApi from '@/api/statistics/call-center/employees-work-schedule'
  import { getFileNameFromContentDispositionHeader } from '@/util/axios'
  import { formatNumberToThousandths } from '@/util/num-format'
  import ProportionChart from '@/components/stat/revenue/ProportionChart.vue'
  import ChartContainer from '@/components/stat/ChartContainer.vue'
  import { formatSecondsToString } from '@/util/helpers'

  export default {
    name: 'EmployeesWorkScheduleStatistics',
    components: {
      ChartContainer,
      ProportionChart,
      Loader,
      StatTopFilter,
      Avatar,
    },
    mixins: [permissions, SnackbarMessage],
    data () {
      return {
        loading: false,
        reportDownloading: false,
        footerProps: { 'items-per-page-options': [15, 25, 50, 100, -1] },
        startDate: null,
        endDate: null,
        filialIds: [],
        filialPreset: null,
        itemsLength: 0,
        itemsPerPage: 15,
        page: 1,
        sortBy: [],
        sortDesc: [],
        proportionChartColors: ['#4DAF4A', '#FF9D00', '#EA306B', '#617798'],
        revenueProportionChartColors: [],
        workingTimeProportionChartColors: [],
        upsellingRevenueProportionChartColors: [],
        headers: [
          {
            text: 'Сотрудник',
            value: 'user',
            align: 'left',
            sortable: false,
          },
          {
            text: 'Кол-во смен',
            value: 'workScheduleCount',
            align: 'center',
          },
          {
            text: 'Часы',
            value: 'workingTime',
            align: 'center',
          },
          {
            text: 'Обработано заказов',
            value: 'acceptedOrdersCount',
            align: 'center',
          },
          { text: 'Допродажи', value: 'upsellingRevenue', align: 'center' },
          {
            text: '% Допродажи',
            value: 'upsellingRevenuePercentage',
            align: 'center',
            sortable: false,
          },
          { text: 'СВП', value: 'avgConfirmTime', align: 'center' },
        ],
        statisticData: [],
        proportionByRevenue: [],
        proportionByWorkingTime: [],
        proportionByUpsellingRevenue: [],
      }
    },
    computed: {
      ...mapGetters({
        globalFilialId: 'branches/getGlobalFilialId',
      }),
    },
    methods: {
      isEmptyProportion (arr) {
        return arr.every((item) => !item.percent)
      },
      formatWorkingTime (workingTime) {
        const hours = Math.floor(workingTime / 60)
        const minutes = workingTime % 60
        let result = `${hours} ч`

        if (minutes) {
          result += ` ${minutes < 10 ? '0' + minutes : minutes} мин`
        }

        return result
      },
      formatAvgConfirmTime (data) {
        return formatSecondsToString(data)
      },
      formatCount (value) {
        return formatNumberToThousandths(value)
      },
      optionsChanged (options) {
        if (
          this.page !== options.page ||
          this.itemsPerPage !== options.itemsPerPage
        ) {
          return true
        }

        if (
          this.sortBy.length !== options.sortBy.length ||
          this.sortBy.some((item) => !options.sortBy.includes(item))
        ) {
          return true
        }

        return (
          this.sortDesc.length !== options.sortDesc.length ||
          this.sortDesc.some((item) => !options.sortDesc.includes(item))
        )
      },
      loadItems (data) {
        if (!this.optionsChanged(data)) {
          return
        }

        this.itemsPerPage = data.itemsPerPage
        this.page = data.page
        this.sortBy = data.sortBy
        this.sortDesc = data.sortDesc

        const payload = {
          from: this.startDate,
          to: this.endDate,
          filialIds: this.filialIds,
          filialPreset: this.filialPreset,
          page: data.page,
          limit: data.itemsPerPage,
          sortBy: data.sortBy,
          sortDesc: data.sortDesc,
        }

        this.fetchData(payload)
      },
      applyFilter (filter) {
        this.startDate = filter.from
        this.endDate = filter.to
        this.filialIds = filter.filialId
        this.filialPreset = filter.filialPreset

        const payload = {
          from: filter.from,
          to: filter.to,
          filialIds: filter.filialId,
          filialPreset: filter.filialPreset,
          page: this.page,
          limit: this.itemsPerPage,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        }

        this.fetchData(payload)
      },
      async fetchData (data) {
        this.loading = true
        this.statisticData = []

        Promise.all([
          employeesWorkScheduleApi.fetchEmployeesWorkScheduleStatistics(data),
          employeesApi.fetchEmployeesRevenueProportion(data),
          employeesApi.fetchEmployeesWorkingTimeProportion(data),
          employeesApi.fetchEmployeesUpsellingRevenueProportion(data),
        ])
          .then((responses) => {
            this.statisticData = responses[0].items
            this.itemsLength = responses[0].meta.total
            this.itemsPerPage = responses[0].meta.limit
            this.proportionByRevenue = this.prepareRevenueProportionData(
              responses[1].items,
            )
            this.proportionByWorkingTime = this.prepareWorkingTimeProportionData(
              responses[2].items,
            )
            this.proportionByUpsellingRevenue = this.prepareUpsellingRevenueProportionData(
              responses[3].items,
            )
            this.revenueProportionChartColors = this.prepareProportionChartColors(
              this.proportionByRevenue,
            )
            this.workingTimeProportionChartColors = this.prepareProportionChartColors(
              this.proportionByWorkingTime,
            )
            this.upsellingRevenueProportionChartColors = this.prepareProportionChartColors(
              this.proportionByUpsellingRevenue,
            )
          })
          .catch((err) => this.showErrorFromResponse(err))
          .finally(() => {
            this.loading = false
          })
      },
      prepareProportionChartColors (items) {
        const colors = []

        for (let i = 0; i < items.length; i++) {
          if (i >= items.length) {
            colors.push(this.proportionChartColors[0])
          } else {
            colors.push(
              items[i].name === 'Остальные'
                ? this.proportionChartColors[
                  this.proportionChartColors.length - 1
                ]
                : this.proportionChartColors[i],
            )
          }
        }

        return colors
      },
      prepareRevenueProportionData (items) {
        return items.map((item) => {
          if (item.value) {
            item.formattedValue = this.$options.filters.amountWithCurrency0(
              item.value,
            )
          }

          return item
        })
      },
      prepareWorkingTimeProportionData (items) {
        return items.map((item) => {
          if (item.value) {
            item.formattedValue = this.formatWorkingTime(item.value)
          }

          return item
        })
      },
      prepareUpsellingRevenueProportionData (items) {
        return items.map((item) => {
          if (item.value) {
            item.formattedValue = `${item.value}%`
          }

          return item
        })
      },
      async downloadReport (filter) {
        this.reportDownloading = true

        const payload = {
          from: filter.from,
          to: filter.to,
          filialIds: filter.filialId,
          filialPreset: filter.filialPreset,
        }

        try {
          const response = await employeesWorkScheduleApi.exportEmployeesWorkScheduleStatistics(
            payload,
          )
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(response.data)
          link.setAttribute(
            'download',
            getFileNameFromContentDispositionHeader(response),
          )
          document.body.appendChild(link)
          link.click()
          link.remove()
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.reportDownloading = false
      },
    },
  }
</script>

<style lang="less" scoped>
@import 'src/assets/styles/statistic.less';

.statistic-datatable {
  ::v-deep {
    .v-data-footer {
      display: flex;
      margin-right: 0;
    }
  }
}

.user-column {
  display: inline-flex;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>
